import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Ticker } from "./Ticker";

const LINKS = [
  { label: "About", to: "/about", id: "about" },
  { label: "Process", to: "/process", id: "process" },
  { label: "Commodities", hash: "#commodities", id: "commodities" },
  { label: "Compliance", to: "/compliance", id: "compliance" },
  { label: "Insights", to: "/insights", id: "insights" },
  { label: "Contact", hash: "#contact", id: "contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const anchor = (l, testid, cls, onClick) => {
    if (l.to)
      return <Link key={l.id} to={l.to} data-testid={testid} onClick={onClick} className={cls}>{l.label}</Link>;
    return isHome ? (
      <a key={l.id} href={l.hash} data-testid={testid} onClick={onClick} className={cls}>{l.label}</a>
    ) : (
      <Link key={l.id} to={`/${l.hash}`} data-testid={testid} onClick={onClick} className={cls}>{l.label}</Link>
    );
  };

  return (
    <header data-testid="site-nav" className="fixed top-0 inset-x-0 z-50">
      <Ticker />
      <div className={`transition-colors duration-300 ${scrolled || !isHome ? "bg-[#0A0A0B]/85 backdrop-blur-xl hairline-b" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <img src="/assets/logo-nav.webp" alt="Zorem Commodities emblem" width="30" height="45" className="h-10 w-auto object-contain" />
            <span className="font-display text-sm tracking-[0.35em] text-[#F4F4F6] group-hover:text-[#D4AF37] transition-colors">
              ZOREM
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links" aria-label="Primary">
            {LINKS.map((l) => anchor(l, `nav-link-${l.id}`, "text-[13px] uppercase tracking-[0.18em] text-zinc-400 hover:text-[#D4AF37] transition-colors"))}
          </nav>

          <div className="flex items-center gap-3">
            {pathname === "/fr" ? (
              <Link to="/" data-testid="nav-lang-toggle" className="hidden lg:inline-flex items-center px-3 h-11 font-mono2 text-[11px] tracking-[0.2em] text-zinc-400 hover:text-[#D4AF37] transition-colors">EN</Link>
            ) : (
              <Link to="/fr" data-testid="nav-lang-toggle" className="hidden lg:inline-flex items-center px-3 h-11 font-mono2 text-[11px] tracking-[0.2em] text-zinc-400 hover:text-[#D4AF37] transition-colors">FR</Link>
            )}
            <Link to="/mandate" data-testid="nav-cta-request-allocation" className="hidden lg:inline-flex items-center px-6 h-11 text-[12px] uppercase tracking-[0.2em] font-medium text-[#0A0A0B] bg-gradient-to-r from-[#C9A227] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#E8C85A] transition-colors">
              Submit a Mandate
            </Link>
            <button
              data-testid="nav-mobile-toggle"
              className="lg:hidden text-zinc-300 hover:text-[#D4AF37] transition-colors"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              aria-expanded={open}
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden bg-[#0A0A0B]/95 backdrop-blur-xl hairline-t" data-testid="nav-mobile-menu">
            <nav className="px-6 py-6 flex flex-col gap-5" aria-label="Mobile">
              {LINKS.map((l) => anchor(l, `nav-mobile-link-${l.id}`, "text-sm uppercase tracking-[0.2em] text-zinc-300 hover:text-[#D4AF37] transition-colors", () => setOpen(false)))}
              <Link to="/mandate" data-testid="nav-mobile-cta" onClick={() => setOpen(false)} className="mt-2 inline-flex justify-center items-center px-6 h-11 text-[12px] uppercase tracking-[0.2em] font-medium text-[#0A0A0B] bg-gradient-to-r from-[#C9A227] to-[#D4AF37]">
                Submit a Mandate
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
